import { otto } from "@gr-exports/async/nexus";
import { defineStore } from "@gr-common/async/nexus";
import { diff } from "@otto-ec/assets-core-utils/array-web";
import { now } from "@gr-common/async/date";
import type { NavigationAsync, NavigationScopeAsync } from "../index.js";
import type { FunctionImplementation } from "../../nexus/function/function.types.js";
import type {
  NavigationScope,
  NavigationScopeName,
  NavigationScopeOptions,
} from "./scope.types.js";
import { asyncScope, defaultEntryComparator, NAV_META, NAV_SCOPES } from "./utils.js";
import { current, getCurrentNavigation } from "./navigation.js";
import type { NavigationState, StateManaged, StateRecord } from "../data/state.types.js";
import { locationState, sanitizeState } from "../data/state.js";
import { defineContext } from "./history.js";
import type { NavigationParameters, ParameterRecord } from "../data/params.types.js";
import type { AsyncNamespace } from "../../nexus/namespace/namespace.types.js";

const log = asyncScope.scope("scope");

/**
 *
 */
type AnyNavigationScope = NavigationScope<NavigationScopeName, NavigationScopeOptions>;

/**
 *
 *
 *
 */
export const scopeCache = new Map<string, AnyNavigationScope>();

/**
 *
 */
export const scopesFiltered = new Map<string, Set<AnyNavigationScope>>();

/**
 *
 */
export const hashFiltered = new Map<string, Set<AnyNavigationScope>>();

/**
 *
 */
export const searchFiltered = new Map<string, Set<AnyNavigationScope>>();

/**
 *
 *
 *
 *
 *
 *
 */
export const getScopedState = /*           */ (
  scope: string,
  state: StateManaged,
): NavigationState<StateRecord> => locationState(state[NAV_SCOPES][scope]);

export const setScope = /*           */ (
  scope: string,
  state: StateManaged,
  value: StateRecord,
): void => {
  state[NAV_META][scope] = now();
  state[NAV_SCOPES][scope] = value;
};

/**
 *
 *
 *
 */
const ensureEntry = /*           */ <T>(map: Map<string, Set<T>>, key: string): Set<T> => {
  if (!map.has(key)) map.set(key, new Set());
  return map.get(key)!;
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export const initializeNamespaceWithCurrentNavigation = /*           */ (
  namespace: AsyncNamespace<NavigationScopeAsync>,
  state: NavigationState<StateRecord>,
  hash: NavigationParameters<ParameterRecord>,
  search: NavigationParameters<ParameterRecord>,
): void => {
  /*                                                         */
  if (!namespace.isReady) {
    namespace.assignNamespace({
      current: defineStore({ state, hash, search }),
      currentHash: defineStore(hash),
      currentSearch: defineStore(search),
      currentState: defineStore(state),
    });
  }
};

/**
 *
 *
 *
 */
export const scope = (<N extends NavigationScopeName, O extends NavigationScopeOptions>(
  name: N,
  options: O = {} as O,
) => {
  /*                                                                                       */
  const { hash: hashOptions = [], search: searchOptions = [] } = options;

  const scopeId = `${name}:${hashOptions.join("|")}:${searchOptions.join("|")}`;
  log.info("Aquiring scope", scopeId, "with options", options);

  /*                                              */
  if (scopeCache.has(scopeId)) return scopeCache.get(scopeId)!;

  /*                                              */
  const namespace = otto.navigation[NAV_SCOPES][scopeId];

  /*                                                         */
  const [, state, hash, search] = getCurrentNavigation(undefined, hashOptions, searchOptions);
  const scopeState = getScopedState(name, state);
  initializeNamespaceWithCurrentNavigation(
    namespace,
    scopeState.clone(),
    hash.clone(),
    search.clone(),
  );

  /*                     */
  const res: NavigationScope<string, NavigationScopeOptions> = {
    name,
    key: scopeId,
    current: namespace.current,
    currentHash: namespace.currentHash,
    currentSearch: namespace.currentSearch,
    currentState: namespace.currentState,

    hash(params) {
      if (params) hash.fromObject(params);
      return hash as never;
    },

    search(params) {
      if (params) search.fromObject(params);
      return search as never;
    },

    state(params) {
      if (params) scopeState.fromObject(params as never);
      return scopeState as never;
    },

    navigate(urlOrString = location.href, o = {}) {
      const { history: navigationType = "push" } = o;
      log.info("Navigating to", urlOrString, "with options", o);

      /*                                                                */
      const method = history[`${navigationType}State`];
      if (!method) return false;

      /*                                                       */
      const nextUrl = new URL(urlOrString, location.href);

      /*                                                         */
      nextUrl.hash = hash.toString();
      nextUrl.search = search.toString();

      /*                            */
      const stateManaged = sanitizeState(o.clear ? {} : history.state);
      setScope(name, stateManaged, scopeState.toObject());

      /*                                                           */

      method.call(defineContext({ ...o, scope: name }), stateManaged, "", nextUrl);
      return true;
    },

    push: (o, u) => res.navigate(u, { ...o, history: "push" }),
    replace: (o, u) => res.navigate(u, { ...o, history: "replace" }),

    back: (o) => history.back.call(defineContext({ ...o, scope: name })) ?? true,
    forward: (o) => history.forward.call(defineContext({ ...o, scope: name })) ?? true,

    reload: () => location.reload() ?? true,
    go: (delta) => history.go(delta) ?? true,
  };

  /*                                           */
  scopeCache.set(scopeId, res);

  /*                                      */
  ensureEntry(scopesFiltered, name).add(res);
  hashOptions.forEach((ho) => ensureEntry(hashFiltered, ho).add(res));
  searchOptions.forEach((so) => ensureEntry(searchFiltered, so).add(res));

  return res;
}) satisfies FunctionImplementation<NavigationAsync["scope"]>;

/**
 *
 */
export const diffEntries = <T extends [string, unknown]>(a: T[], b: T[]): T[] =>
  diff(a, b, defaultEntryComparator)[2];

/**
 *
 *
 *
 *
 */
export function initScopes(): void {
  current.subscribe(
    (cur, old) => {
      const hashTargets = new Set<AnyNavigationScope>();
      const searchTargets = new Set<AnyNavigationScope>();
      const scopeTargets = new Set<AnyNavigationScope>();

      /*                                          */
      const changedHashes = diffEntries(cur.hash.toEntries(), old!.hash.toEntries());
      changedHashes.forEach(([key]) => hashFiltered.get(key)?.forEach((t) => hashTargets.add(t)));

      const changedSearch = diffEntries(cur.search.toEntries(), old!.search.toEntries());
      changedSearch.forEach(([key]) =>
        searchFiltered.get(key)?.forEach((t) => searchTargets.add(t)),
      );

      const changedScopes = diffEntries(
        Object.entries(cur.state[NAV_META]),
        Object.entries(old!.state[NAV_META]),
      );
      changedScopes.forEach(([key]) =>
        scopesFiltered.get(key)?.forEach((t) => scopeTargets.add(t)),
      );

      /*                                                             */
      const curScopes = cur.state[NAV_SCOPES];
      const allTargets = new Set([...hashTargets, ...searchTargets, ...scopeTargets]);

      /*                                        */
      allTargets.forEach((t) => {
        /*                                                                                                     */
        const state = t.state(curScopes[t.name] ?? {}).clone();
        const hash = t.hash(cur.hash.toRecord()).clone();
        const search = t.search(cur.search.toRecord()).clone();

        /*                                                   */
        t.current.set({ hash, search, state, info: cur.info });
      });

      /*                                                */
      /*                                                                            */
      hashTargets.forEach((t) => t.currentHash.set(t.current.get().hash));
      searchTargets.forEach((t) => t.currentSearch.set(t.current.get().search));
      scopeTargets.forEach((t) => t.currentState.set(t.current.get().state));
    },
    { skipRetained: true },
  );
}
